import { createContext, Dispatch, ReactNode } from "react";

export interface TooltipConfig {
    shortcutKey: string;
    toolName: string;
    /** Pre-translated display text. When provided, skips the i18n lookup. */
    label?: string;
    alwaysVisible?: boolean;
    placement?: "top" | "bottom" | "left" | "right";
    /**
     * When true, the tooltip is shown based on hovering the individual button
     * instead of the column/area-level hover used by standard toolbars.
     */
    showOnHover?: boolean;
}

/** Fields shared by every toolbar entry, regardless of kind. */
export type ToolbarEntryCommon = {
    id: string;
    position?: number;
};

/**
 * A regular, interactive toolbar button. This is the `separator: false`
 * variant of the {@link ToolbarButtonConfig} discriminated union.
 */
export type ToolbarButtonConfig = ToolbarEntryCommon & {
    separator?: false;
    icon: ReactNode;
    onClick?: () => void;
    title?: string;
    /**
     * Optional ref to attach to the button element
     */
    ref?: React.Ref<HTMLButtonElement>;
    /**
     * Shows a small expander icon in a corner
     * Useful for buttons that open menus or have subactions
     */
    hasExpander?: boolean;
    /**
     * Position of the expander icon. Defaults to "bottom-right"
     */
    expanderPosition?: "bottom-left" | "bottom-right";
    /**
     * Optional custom component to render instead of DefaultIconButton.
     * When provided, the component will receive onClick, title, and children (icon) props.
     * The component should be compatible with these props but can accept additional ones.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component?: React.ComponentType<any>;
    /**
     * Tooltip configuration for rich tooltips with keyboard shortcuts
     */
    tooltip?: TooltipConfig;
};

/**
 * A non-interactive separator (divider). It participates in ordering and
 * removal like a button, but carries no `icon`/`onClick`; consumers decide how
 * to render it.
 */
export type ToolbarSeparatorConfig = ToolbarEntryCommon & {
    separator: true;
};

/** Discriminated union of everything that can live in a toolbar's order. */
export type ToolbarEntryConfig = ToolbarButtonConfig | ToolbarSeparatorConfig;

export type ToolbarButtonState = {
    active: boolean;
    disabled: boolean;
    visible: boolean;
};

export type ToolbarButtonEntry = {
    config: ToolbarEntryConfig;
    state: ToolbarButtonState;
};

export type ToolbarEntry = {
    id: string;
    buttons: Record<string, ToolbarButtonEntry>;
    order: string[];
};

export type State = Record<string, ToolbarEntry>;

export const initialState = {} as Record<string, ToolbarEntry>;

enum ActionTypes {
    AddToolbar,
    RemoveToolbar,
    AddButton,
    RemoveButton,
    UpdateButtonConfig,
    UpdateButtonState,
    BulkUpdateButtonStates,
    ReorderToolbar,
}

function addToolbar(config: { id: string; buttons: ToolbarButtonEntry[] }) {
    return {
        type: ActionTypes.AddToolbar as const,
        config,
    };
}

function removeToolbar(toolbarId: string) {
    return {
        type: ActionTypes.RemoveToolbar as const,
        toolbarId,
    };
}

function addButton(toolbarId: string, buttonEntry: ToolbarButtonEntry) {
    return {
        type: ActionTypes.AddButton as const,
        toolbarId,
        buttonEntry,
    };
}

function removeButton(toolbarId: string, buttonId: string) {
    return {
        type: ActionTypes.RemoveButton as const,
        toolbarId,
        buttonId,
    };
}

function updateButtonConfig(toolbarId: string, buttonId: string, config: Partial<ToolbarButtonConfig>) {
    return {
        type: ActionTypes.UpdateButtonConfig as const,
        toolbarId,
        buttonId,
        config,
    };
}

function updateButtonState(
    toolbarId: string,
    buttonId: string,
    state: { active?: boolean; disabled?: boolean; visible?: boolean },
) {
    return {
        type: ActionTypes.UpdateButtonState as const,
        toolbarId,
        buttonId,
        state,
    };
}

function bulkUpdateButtonStates(
    toolbarId: string,
    updates: Record<string, { active?: boolean; disabled?: boolean; visible?: boolean }>,
) {
    return {
        type: ActionTypes.BulkUpdateButtonStates as const,
        toolbarId,
        updates,
    };
}

function reorderToolbar(toolbarId: string, buttonOrder: string[]) {
    return {
        type: ActionTypes.ReorderToolbar as const,
        toolbarId,
        buttonOrder,
    };
}

export const actions = {
    addToolbar,
    removeToolbar,
    addButton,
    removeButton,
    updateButtonConfig,
    updateButtonState,
    bulkUpdateButtonStates,
    reorderToolbar,
};

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchToolbars = Dispatch<Actions>;

export const StateContext = createContext<State>({});
export const DispatchContext = createContext<DispatchToolbars | undefined>(undefined);

function insertButtonAtPosition(order: string[], buttonId: string, position: number): string[] {
    // Clamp position to valid range
    const insertIndex = Math.max(0, Math.min(position, order.length));
    const newOrder = [...order];
    newOrder.splice(insertIndex, 0, buttonId);
    return newOrder;
}

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.AddToolbar: {
            const toolbarId = action.config.id;

            // Add-only: If toolbar already exists, warn and skip
            if (state[toolbarId]) {
                console.warn(`Toolbar "${toolbarId}" already exists. Use updateButtonState for state changes.`);
                return state;
            }

            // Initialize toolbar with its buttons (already complete ButtonEntry objects)
            const buttons = action.config.buttons.reduce(
                (acc, buttonEntry) => {
                    acc[buttonEntry.config.id] = buttonEntry;
                    return acc;
                },
                {} as Record<string, ToolbarButtonEntry>,
            );

            const order = action.config.buttons.map((buttonEntry) => buttonEntry.config.id);

            return {
                ...state,
                [toolbarId]: {
                    id: toolbarId,
                    buttons,
                    order,
                },
            };
        }

        case ActionTypes.RemoveToolbar: {
            const { [action.toolbarId]: _removed, ...remainingToolbars } = state;
            return remainingToolbars;
        }

        case ActionTypes.AddButton: {
            const toolbarEntry = state[action.toolbarId];

            if (!toolbarEntry) {
                console.warn(`Toolbar "${action.toolbarId}" is not registered`);
                return state;
            }

            const updatedButtons = {
                ...toolbarEntry.buttons,
                [action.buttonEntry.config.id]: action.buttonEntry,
            };

            // Re-adding a registered id replaces it, so hosts can safely re-register on effect re-runs
            const existingIndex = toolbarEntry.order.indexOf(action.buttonEntry.config.id);
            const order = toolbarEntry.order.filter((id) => id !== action.buttonEntry.config.id);

            // Insert at the specified position; otherwise keep a re-added button's slot, or append a new one
            const position =
                action.buttonEntry.config.position ?? (existingIndex === -1 ? order.length : existingIndex);
            const updatedOrder = insertButtonAtPosition(order, action.buttonEntry.config.id, position);

            return {
                ...state,
                [action.toolbarId]: {
                    ...toolbarEntry,
                    buttons: updatedButtons,
                    order: updatedOrder,
                },
            };
        }

        case ActionTypes.RemoveButton: {
            const toolbarEntry = state[action.toolbarId];

            if (!toolbarEntry) {
                console.warn(`Toolbar "${action.toolbarId}" is not registered`);
                return state;
            }

            const { [action.buttonId]: _removed, ...remainingButtons } = toolbarEntry.buttons;
            const updatedOrder = toolbarEntry.order.filter((id) => id !== action.buttonId);

            return {
                ...state,
                [action.toolbarId]: {
                    ...toolbarEntry,
                    buttons: remainingButtons,
                    order: updatedOrder,
                },
            };
        }

        case ActionTypes.UpdateButtonConfig: {
            const toolbarEntry = state[action.toolbarId];

            if (!toolbarEntry || !toolbarEntry.buttons[action.buttonId]) {
                console.warn(`Button "${action.buttonId}" in toolbar "${action.toolbarId}" is not registered`);
                return state;
            }

            const updatedButton = {
                ...toolbarEntry.buttons[action.buttonId],
                // Spreading across the config union widens `separator` to
                // `boolean`; the merged shape is still a valid entry config.
                config: {
                    ...toolbarEntry.buttons[action.buttonId].config,
                    ...action.config,
                } as ToolbarEntryConfig,
            };

            return {
                ...state,
                [action.toolbarId]: {
                    ...toolbarEntry,
                    buttons: {
                        ...toolbarEntry.buttons,
                        [action.buttonId]: updatedButton,
                    },
                },
            };
        }

        case ActionTypes.UpdateButtonState: {
            const toolbarEntry = state[action.toolbarId];

            if (!toolbarEntry || !toolbarEntry.buttons[action.buttonId]) {
                console.warn(`Button "${action.buttonId}" in toolbar "${action.toolbarId}" is not registered`);
                return state;
            }

            const updatedButton = {
                ...toolbarEntry.buttons[action.buttonId],
                state: {
                    ...toolbarEntry.buttons[action.buttonId].state,
                    ...action.state,
                },
            };

            return {
                ...state,
                [action.toolbarId]: {
                    ...toolbarEntry,
                    buttons: {
                        ...toolbarEntry.buttons,
                        [action.buttonId]: updatedButton,
                    },
                },
            };
        }

        case ActionTypes.BulkUpdateButtonStates: {
            const toolbarEntry = state[action.toolbarId];

            if (!toolbarEntry) {
                console.warn(`Toolbar "${action.toolbarId}" not found for bulk update`);
                return state;
            }

            const updatedButtons = { ...toolbarEntry.buttons };
            let hasChanges = false;

            // Update each button state
            Object.entries(action.updates).forEach(([buttonId, stateUpdate]) => {
                if (updatedButtons[buttonId]) {
                    updatedButtons[buttonId] = {
                        ...updatedButtons[buttonId],
                        state: {
                            ...updatedButtons[buttonId].state,
                            ...stateUpdate,
                        },
                    };
                    hasChanges = true;
                } else {
                    console.warn(`Button "${buttonId}" in toolbar "${action.toolbarId}" not found`);
                }
            });

            if (!hasChanges) {
                return state;
            }

            return {
                ...state,
                [action.toolbarId]: {
                    ...toolbarEntry,
                    buttons: updatedButtons,
                },
            };
        }

        case ActionTypes.ReorderToolbar: {
            const toolbarEntry = state[action.toolbarId];

            if (!toolbarEntry) {
                console.warn(`Toolbar "${action.toolbarId}" is not registered`);
                return state;
            }

            const existingButtonIds = Object.keys(toolbarEntry.buttons);
            const providedButtonIds = action.buttonOrder;

            // HARD REQUIREMENT: All provided button IDs must exist in the toolbar
            const invalidButtonIds = providedButtonIds.filter((buttonId) => !existingButtonIds.includes(buttonId));
            if (invalidButtonIds.length > 0) {
                console.error(
                    `Reorder failed: Invalid button IDs for toolbar "${action.toolbarId}": ${invalidButtonIds.join(", ")}`,
                );
                return state;
            }

            // HARD REQUIREMENT: All existing button IDs must be included in the new order
            const missingButtonIds = existingButtonIds.filter((buttonId) => !providedButtonIds.includes(buttonId));
            if (missingButtonIds.length > 0) {
                console.error(
                    `Reorder failed: Missing button IDs for toolbar "${action.toolbarId}": ${missingButtonIds.join(", ")}`,
                );
                return state;
            }

            // HARD REQUIREMENT: No duplicate button IDs allowed
            const duplicateIds = providedButtonIds.filter((id, index) => providedButtonIds.indexOf(id) !== index);
            if (duplicateIds.length > 0) {
                console.error(
                    `Reorder failed: Duplicate button IDs for toolbar "${action.toolbarId}": ${[...new Set(duplicateIds)].join(", ")}`,
                );
                return state;
            }

            // All validations passed - apply the new order
            return {
                ...state,
                [action.toolbarId]: {
                    ...toolbarEntry,
                    order: action.buttonOrder,
                },
            };
        }

        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
